import * as React from 'react';
import { storyblokEditable, type SbBlokData } from '@storyblok/react';

import type { BoardMemberStoryblok } from '../../types/storyblok';

interface BoardMemberProps {
  blok: BoardMemberStoryblok;
}

/**
 * BoardMember displays a single board member with logo, role, name, and position.
 *
 * Per Figma:
 * - Logo: 90x77px, rounded-lg (8px), 1px gray border
 * - Role: 14px bold, gray-700 (e.g., "GMA Chair")
 * - Name: 16px extrabold, brand-deep
 * - Position: 12px normal, gray-700 (e.g., "President & CEO, AAA Mountain West Group")
 *
 * Title field uses " · " separator: "Role · Position"
 */
export const BoardMember: React.FC<BoardMemberProps> = ({ blok }) => {
  const photoSrc = blok.photo?.filename || undefined;
  const photoAlt = blok.photo?.alt ?? '';

  // Split title on " · " to get role and position
  const [role, position] = blok.title?.split(' · ') ?? ['', ''];

  return (
    <div
      {...storyblokEditable(blok as unknown as SbBlokData)}
      className="flex flex-col items-center gap-6 w-[159px]"
    >
      {/* Logo with rounded corners and border */}
      <div className="w-[90px] h-[77px] shrink-0 rounded-lg overflow-hidden bg-neutral-white border border-gray-300">
        {photoSrc && (
          <img src={photoSrc} alt={photoAlt} className="w-full h-full object-contain p-1" />
        )}
      </div>

      {/* Role, Name, and Position */}
      <div className="flex flex-col items-center gap-2 text-center w-full">
        {role && <span className="text-sm font-bold text-gray-700">{role}</span>}
        <span className="font-extrabold text-base text-brand-deep">{blok.name}</span>
        {position && <span className="text-xs text-gray-700">{position}</span>}
      </div>
    </div>
  );
};
